import React from "react";
import get from "lodash/get";
import map from "lodash/map";
import moment from "moment";
import { withTranslation } from "react-i18next";
import { RillContext } from "../../../rill/RillReact/share/base";

import BuildProgramStepOneModal from "../../../parts/modals/add-cycle";
import BuildProgramStepTwoModal from "../../../parts/modals/program-settings";
import AddToCalendarModal from "../../../parts/modals/add-to-calendar";
import LearnMoreModal from "../../../parts/modals/learn-more";

import ProgramCard from "../../../parts/cards/program-card";
import CycleCard from "../../../parts/cards/cycle-card";
import sortPrograms from "../../../utils/sort-programs";
import { isPushOrPerformClient } from "../../../utils/user-helpers";
import Pagination from "../../../parts/pagination";
import EmptyStatementMsg from "../calendar/components/EmptyStatementMsg";
import { getUserToRequest } from "../settings/manage-coaches/utils/manage-coaches-utils";
import SvgIcon from "../../../parts/SvgIcon";
import DropdownIconsCheck from "../../../parts/inputs/dropdown-icons-check";

const pageSize = 50;

class View extends React.Component {
  submitSort(sort) {
    submit(`/actions/refresh?`, {
      method: "GET",
      body: {
        sort,
      },
    });
  }
  render() {
    const { t } = this.props;
    const { locals } = this.context;
    const sortItems = [
      {
        title: t("programBuilder.dateAdded"),
        src: "/img/svg/arrow-to-bottom.svg",
        alt: "createdDate",
        className: "icon-white",
        id: "createdDate",
      },
      {
        title: t("programBuilder.aToZ"),
        src: "/img/svg/sort-amount-up.svg",
        alt: "name",
        className: "icon-white",
        id: "name",
      },
      {
        title: t("programBuilder.zToA"),
        src: "/img/svg/sort-amount-down.svg",
        alt: "nameDescending",
        className: "icon-white",
        id: "nameDescending",
      },
    ];
    const { activeUser, organizationOwner } = locals;
    const userToRequest = getUserToRequest(activeUser, organizationOwner);
    const { subscription, membership } = userToRequest;
    return (
      <main className="athlete-programs">
        {!subscription && !membership && (
          <>
            <header className="force-mobile-hidden">
              <div>
                <DropdownIconsCheck
                  items={sortItems}
                  action={(sort) => {
                    submit(`/actions/refresh`, {
                      method: "GET",
                      body: { sort },
                    });
                  }}
                  title={t("programBuilder.sort")}
                />
                <a
                  role="button"
                  data-button-type="primary"
                  href={`/actions/refresh/?modal[type]=build-step-one&cycleType=new`}
                >
                  <SvgIcon
                    noFilterOnLight={true}
                    src="/img/new-icons/add.svg"
                  />
                  {t("programBuilder.buildProgram")}
                </a>
              </div>
            </header>
            <header className="force-only-show-mobile">
              <div className="header-anchors-programs">
                <a
                  role="button"
                  data-button-type="primary"
                  href={`/actions/refresh/?modal[type]=build-step-one&cycleType=new`}
                >
                  <SvgIcon
                    noFilterOnLight={true}
                    src="/img/new-icons/add.svg"
                  />
                  {t("programBuilder.buildProgram")}
                </a>
              </div>
              <div className="header-legends-programs">
                <DropdownIconsCheck
                  items={sortItems}
                  action={(sort) => {
                    submit(`/actions/refresh`, {
                      method: "GET",
                      body: { sort },
                    });
                  }}
                  title={t("programBuilder.sort")}
                />
              </div>
            </header>
          </>
        )}

        <section className="Main">
          {this.props.cards && this.props.cards.length > 0 ? (
            <div className="columns">
              {locals.query["program-view"] === "programs"
                ? map(this.props.cards, (program, cardIndex) => {
                    return (
                      <ProgramCard
                        key={program._id}
                        program={program}
                        cardIndex={cardIndex}
                      />
                    );
                  })
                : map(this.props.cards, (cycle, cardIndex) => {
                    return (
                      <CycleCard
                        key={cycle._id}
                        cycle={cycle}
                        cardIndex={cardIndex}
                        isOwnCycle={
                          cycle.owner === locals.activeUserToRequest._id
                        }
                      />
                    );
                  })}
            </div>
          ) : (
            <div className="empty-cards-program-builder">
              <EmptyStatementMsg message={t("programBuilder.noDataToShow")} />
            </div>
          )}
          <Pagination total={this.props.total} pageSize={pageSize} />
        </section>
      </main>
    );
  }
}

View.contextType = RillContext;

const ProgramBuilder = (app) => {
  app.get("/", async ({ res, req, locals, load }) => {
    const { query } = req;

    const page = query.page - 1 || 0;
    const organizationOwner = locals.organizationOwner;
    const activeUser = locals.activeUser;
    const userToRequest = getUserToRequest(activeUser, organizationOwner);
    const isSubscriber = isPushOrPerformClient(userToRequest);

    const isMember = userToRequest.membership ? true : false;
    await load("activeCycles", {
      customSkip: pageSize * page,
      customLimit: pageSize,
      fromMyPrograms: !isSubscriber || !isMember,
      feCycles: isSubscriber || isMember,
    });
    await load("activeCyclesCount", {
      fromMyPrograms: !isSubscriber || !isMember,
      feCycles: isSubscriber || isMember,
    });

    const modal = get(query, "modal", {});
    switch (modal.type) {
      case "edit-cycle-settings":
        await load("editCycle", { cycle: query.cycleId });
        break;
      case "edit-program-settings":
        await load("editCycle", { cycle: query.cycleId });
        break;
      case "add-to-calendar":
        await load("editCycle", {
          cycle: query.cycleId || query.cycle,
          feCycles: isSubscriber || isMember,
        });
        await load("activeExercises", {
          start: moment(-1),
          end: moment("December 30 2049"),
        });
        break;
      case "learn-more":
        await load("editCycle", {
          cycle: query.cycleId,
          feCycles: isSubscriber || isMember,
        });
        await load("programExercises", { programId: query.cycleId });
        break;
      default:
        break;
    }

    locals.modal = {
      "build-step-one": <BuildProgramStepOneModal />,
      "build-step-two": <BuildProgramStepTwoModal />,
      "edit-cycle-settings": <BuildProgramStepOneModal />,
      "edit-program-settings": <BuildProgramStepOneModal />,
      "add-to-calendar": <AddToCalendarModal />,
      "learn-more": <LearnMoreModal fromAthlete />,
    }[modal.type];

    query.sort = query.sort || "createdDate";

    locals.cards =
      isSubscriber || isMember
        ? locals.activeCycles
        : sortPrograms(
            locals.activeCycles,
            query.sort,
            query["program-type"],
            query["program-progress"],
            query["program-view"]
          );
    res.body = (
      <ViewWithI18n cards={locals.cards} total={locals.activeCyclesCount.count} />
    );
  });
};

const ViewWithI18n = withTranslation("dashboard")(View);

export default ProgramBuilder;
